import React, { useMemo, useState } from 'react';

import { PlanData, PlanSkill, PlanTest, fmtDate } from './types';

const stripKind = (name: string) => name.replace(/^Digital (SAT|ACT) /, '');

// ── Score by Test ──────────────────────────────────────────────────────────
// Driven by the section config:
//   SAT  — stacks two sections into the total (1600) under an "All" view.
//   ACT  — composite = average, no additive total → scalar Composite + STEM
//          views (single bars, 1–36) instead of "All".
//   SHSAT (meta.trendUnit 'percent') — the 100-q and 114-q forms aren't
//          comparable raw, so bars plot % correct (0–100%) and are labelled
//          with the raw "X / N"; "All" = overall %, sections = section %.
// Per-section views otherwise show single bars to the section ceiling.
const COMPOSITE_COLOR = '#1C2434';
const STEM_COLOR = '#0f7b6c';

function ScoreChart({ data }: { data: PlanData }) {
    const tests = data.tests;
    const { meta } = data;
    const percentMode = meta.trendUnit === 'percent';
    const [hover, setHover] = useState<number | null>(null);

    // Scalar metric views (ACT): Composite + STEM. SAT uses its stacked "All".
    const metricViews = meta.testKind === 'act'
        ? [
            { key: 'composite', label: meta.compositeLabel, color: COMPOSITE_COLOR, get: (t: PlanTest) => t.composite },
            ...(meta.hasStem ? [{ key: 'stem', label: 'STEM', color: STEM_COLOR, get: (t: PlanTest) => t.stem }] : []),
        ]
        : [];

    const [view, setView] = useState<string>(meta.testKind === 'act' ? 'composite' : 'all');
    if (!tests.length) return null;

    const N = tests.length;

    const activeMetric = metricViews.find(m => m.key === view);
    const isMetric = !!activeMetric;                       // ACT Composite / STEM scalar view
    // "All" stacks the sections for additive scores: SAT stacks raw section
    // scores to the 1600 total; SHSAT (percent mode) stacks each section's
    // contribution to overall % correct (ela/max + math/max = overall %).
    const stacked = view === 'all';
    const isSection = !isMetric && view !== 'all';
    const section = isSection ? meta.sections.find(s => s.key === view) : undefined;

    // Percent helpers (SHSAT): % and the raw "X / N" label for a given key.
    const pctValue = (t: PlanTest, key: string): number | null => {
        if (key === 'all') return t.composite != null && t.compositeMax ? (t.composite / t.compositeMax) * 100 : null;
        const sc = t.sectionScores[key]; const mx = t.sectionMax?.[key];
        return sc != null && mx ? (sc / mx) * 100 : null;
    };
    const rawLabel = (t: PlanTest, key: string): string | null => {
        if (key === 'all') return t.composite != null && t.compositeMax != null ? `${t.composite}/${t.compositeMax}` : null;
        const sc = t.sectionScores[key]; const mx = t.sectionMax?.[key];
        return sc != null && mx != null ? `${sc}/${mx}` : null;
    };

    const yMax = percentMode ? 100
        : stacked ? meta.compositeCeiling
        : isMetric ? meta.compositeCeiling
        : (section?.ceiling ?? 800);
    const grid = [0.25, 0.5, 0.75, 1].map(f => Math.round(yMax * f));

    const valueOf = (t: PlanTest): number | null =>
        percentMode ? pctValue(t, view)
        : stacked ? t.composite
        : isMetric ? activeMetric!.get(t)
        : (t.sectionScores[view] ?? null);
    const barColor = percentMode
        ? (view === 'all' ? COMPOSITE_COLOR : (meta.sections.find(s => s.key === view)?.color ?? COMPOSITE_COLOR))
        : isMetric ? activeMetric!.color : (section?.color ?? COMPOSITE_COLOR);

    const shown = tests.map(valueOf).filter((v): v is number => v != null);
    const avg = shown.length ? Math.round(shown.reduce((a, v) => a + v, 0) / shown.length) : 0;
    const avgLabel = `Avg ${avg}${percentMode ? '%' : ''}`;

    // Geometry. The average label sits in a right-hand gutter outside the plot
    // (PR is widened to fit it) — drawn inside the plot it landed on top of the
    // first bar and its score label.
    const SLOT = 76;
    const avgGutter = avg > 0 ? Math.round(avgLabel.length * 5.7) + 12 : 0;
    const PL = 44, PR = 20 + avgGutter, PT = 28, PB = 46, H = 300;
    const W = Math.max(560, PL + PR + N * SLOT);
    const iW = W - PL - PR, iH = H - PT - PB;

    const yF = (v: number) => PT + (1 - v / yMax) * iH;
    const hF = (v: number) => (v / yMax) * iH;
    const slot = iW / N;
    const bw = Math.min(46, slot * 0.6);
    const cx = (i: number) => PL + slot * (i + 0.5);

    const sub = percentMode && view === 'all'
        ? 'Sections stacked into the overall % correct per test (raw shown as correct / total) — comparable across the 100- and 114-question forms.'
        : percentMode
        ? `${meta.sections.find(s => s.key === view)?.label} % correct per test (raw shown as correct / total).`
        : stacked
        ? `${meta.sections.map(s => s.label).join(' + ')} stacked into the total. Single-section tests show one segment.`
        : view === 'composite'
        ? 'Each test’s Composite (average of English, Math & Reading).'
        : view === 'stem'
        ? 'Each test’s STEM score (average of Math & Science).'
        : `Showing each test’s ${section?.label} section score.`;

    const chips: [string, string][] = meta.testKind === 'act'
        ? [...metricViews.map(m => [m.key, m.label] as [string, string]), ...meta.sections.map(s => [s.key, s.label] as [string, string])]
        : [['all', 'All'], ...meta.sections.map(s => [s.key, s.label] as [string, string])];

    const gridLabel = (g: number) => (percentMode ? `${g}%` : `${g}`);

    return (
        <div className='r-card'>
            <div className='r-card__head'>
                <div>
                    <div className='r-card__title'>Score by Test</div>
                    <div className='r-card__sub'>{sub} Dashed line is the student’s average.</div>
                </div>
                <div className='r-chips'>
                    {chips.map(([k, l]) => (
                        <button key={k} className={view === k ? 'r-chip r-chip--on' : 'r-chip'} onClick={() => setView(k)}>{l}</button>
                    ))}
                </div>
            </div>
            <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
                    {grid.map(g => (
                        <g key={g}>
                            <line x1={PL} x2={W - PR} y1={yF(g)} y2={yF(g)} stroke='#F1F5F9' />
                            <text x={PL - 8} y={yF(g) + 4} textAnchor='end' fontSize='11' fill='#98a2b3'>{gridLabel(g)}</text>
                        </g>
                    ))}
                    {avg > 0 && (
                        <g>
                            <line x1={PL} x2={W - PR + 6} y1={yF(avg)} y2={yF(avg)} stroke='#828282' strokeDasharray='3,4' strokeOpacity='0.7' />
                            <text x={W - PR + 10} y={yF(avg) + 3.5} fontSize='10' fontWeight='700' fill='#667085'>{avgLabel}</text>
                        </g>
                    )}
                    {tests.map((t, i) => {
                        const on = hover === i;
                        const hoverRect = <rect x={cx(i) - slot / 2} y={PT} width={slot} height={iH} fill='transparent' />;
                        const dateLabel = <text x={cx(i)} y={H - PB + 16} textAnchor='middle' fontSize='10' fill='#98a2b3'>{fmtDate(t.date)}</text>;

                        // stacked (SAT all) vs single bar (SHSAT %, ACT metric, or one section)
                        const x = cx(i) - bw / 2;
                        const segs: { y: number; h: number; fill: string }[] = [];
                        let topY = H - PB, label: number | string | null = null;
                        if (stacked) {
                            let cyB = H - PB;
                            for (const s of meta.sections) {
                                const raw = t.sectionScores[s.key] ?? 0;
                                // Percent mode: segment = this section's share of overall %.
                                const v = percentMode ? (t.compositeMax ? (raw / t.compositeMax) * 100 : 0) : raw;
                                if (v > 0) { const h = hF(v); segs.push({ y: cyB - h, h, fill: s.color }); cyB -= h; }
                            }
                            topY = cyB;
                            label = percentMode ? rawLabel(t, 'all') : t.composite;
                        } else {
                            const v = valueOf(t);
                            if (v != null) { const h = hF(v); segs.push({ y: H - PB - h, h, fill: barColor }); topY = H - PB - h; }
                            label = percentMode ? rawLabel(t, view) : v;
                        }
                        return (
                            <g key={t.studentTestId} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }}>
                                {hoverRect}
                                {segs.map((s, si) => <rect key={si} x={x} y={s.y} width={bw} height={s.h} fill={s.fill} opacity={on ? 1 : 0.9} rx='3' />)}
                                {stacked && segs.length > 1 && <line x1={x} x2={x + bw} y1={segs[segs.length - 1].y} y2={segs[segs.length - 1].y} stroke='#fff' strokeWidth='2' />}
                                {label != null && <text x={cx(i)} y={topY - 8} textAnchor='middle' fontSize='11' fontWeight='700' fill='#1C2434'>{label}</text>}
                                {dateLabel}
                            </g>
                        );
                    })}
                    {hover != null && tests[hover] && (() => {
                        // Tooltip drawn after all bars so it paints on top (SVG has no z-index).
                        const i = hover, t = tests[i];
                        const lineCount = 1 + meta.sections.length + 1 + (meta.hasStem ? 1 : 0) + (percentMode ? 1 : 0);
                        const boxH = lineCount * 16 + 12;
                        // Box grows with the test name so long names don't get cut off.
                        const boxW = Math.max(150, stripKind(t.name).length * 6.4 + 24);
                        const tx = cx(i) > W * 0.7 ? cx(i) - boxW - 12 : cx(i) + 12;
                        const overall = percentMode ? pctValue(t, 'all') : null;
                        let ly = PT + 22;
                        return (
                            <g pointerEvents='none'>
                                <rect x={tx} y={PT + 6} width={boxW} height={boxH} rx='6' fill='#1C2434' />
                                <text x={tx + 12} y={ly} fontSize='11' fontWeight='700' fill='#fff'>{stripKind(t.name)}</text>
                                {meta.sections.map(s => {
                                    ly += 16;
                                    const val = percentMode ? (rawLabel(t, s.key) ?? '—') : (t.sectionScores[s.key] ?? '—');
                                    return <text key={s.key} x={tx + 12} y={ly} fontSize='11' fill='#cdd5e0'>{s.label} {val}</text>;
                                })}
                                {(() => { ly += 16; const val = percentMode ? (rawLabel(t, 'all') ?? '—') : (t.composite ?? '—'); return <text x={tx + 12} y={ly} fontSize='11' fontWeight='700' fill='#fff'>{meta.compositeLabel} {val}</text>; })()}
                                {percentMode && (() => { ly += 16; return <text x={tx + 12} y={ly} fontSize='11' fill='#9fd5c8'>{overall != null ? `${Math.round(overall)}% correct` : '—'}</text>; })()}
                                {meta.hasStem && (() => { ly += 16; return <text x={tx + 12} y={ly} fontSize='11' fill='#9fd5c8'>STEM {t.stem ?? '—'}</text>; })()}
                            </g>
                        );
                    })()}
                </svg>
            </div>
            <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 12, color: '#475467', flexWrap: 'wrap' }}>
                {stacked
                    ? meta.sections.map(s => (
                        <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span className='r-dot' style={{ background: s.color }} />{s.label}</span>
                    ))
                    : <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span className='r-dot' style={{ background: barColor }} />{isMetric ? activeMetric!.label : section?.label}</span>}
            </div>
        </div>
    );
}

// ── Accuracy Over Time ─────────────────────────────────────────────────────
type Trajectory = {
    key: string;
    label: string;
    sub: string;
    color: string;
    dash: string | null;
    w: number;
    points: { i: number; v: number }[];
};

function AccuracyExplorer({ data }: { data: PlanData }) {
    const tests = data.tests;
    const { meta } = data;
    const N = tests.length;

    // Flat taxonomy (ACT): every category is its own domain (domain === skill),
    // so the picker is one level (section → category) with no skill sub-tier.
    const flat = data.skills.length > 0 && data.skills.every(s => s.skill === s.domain);
    const sectionLabel = (key: string) => meta.sections.find(s => s.key === key)?.label || key;

    const skillsByDom = useMemo(() => {
        const m: Record<string, PlanSkill[]> = {};
        data.skills.forEach(s => { (m[s.domain] = m[s.domain] || []).push(s); });
        return m;
    }, [data.skills]);

    const defaultKeys = useMemo(
        () => data.top.slice(0, 3).map(s => (flat ? `dom::${s.domain}` : `skl::${s.domain}::${s.skill}`)),
        [data.top, flat],
    );
    const [selected, setSelected] = useState<Set<string>>(() => new Set(defaultKeys));
    const [openDomains, setOpenDomains] = useState<Set<string>>(() => {
        const s = new Set<string>();
        if (!flat) defaultKeys.forEach(k => s.add(k.split('::')[1]));
        return s;
    });
    const toggleKey = (k: string) => setSelected(prev => {
        const n = new Set(prev);
        if (n.has(k)) n.delete(k); else n.add(k);
        return n;
    });
    const toggleDomain = (name: string) => setOpenDomains(prev => {
        const s = new Set(prev);
        if (s.has(name)) s.delete(name); else s.add(name);
        return s;
    });
    const selectAllInDomain = (domainName: string) => {
        const enabled = (skillsByDom[domainName] || []).filter(s => s.attempted >= 2);
        const keys = [`dom::${domainName}`, ...enabled.map(s => `skl::${domainName}::${s.skill}`)];
        setSelected(prev => {
            const n = new Set(prev);
            const allOn = keys.every(k => n.has(k));
            keys.forEach(k => (allOn ? n.delete(k) : n.add(k)));
            return n;
        });
    };

    const dashSeq = ['6,3', '2,3', '8,4,2,4'];
    const toPoints = (values: (number | null)[]) =>
        values.map((v, i) => (v == null ? null : { i, v })).filter(Boolean) as { i: number; v: number }[];
    const trajectories: Trajectory[] = [];
    data.domains.forEach(d => {
        if (selected.has(`dom::${d.domain}`)) {
            trajectories.push({
                key: `dom::${d.domain}`, label: d.domain,
                sub: flat ? sectionLabel(d.section) : 'Domain overall',
                color: d.color, dash: null, w: 2.5, points: toPoints(d.values),
            });
        }
        if (!flat) {
            let di = 0;
            (skillsByDom[d.domain] || []).forEach(s => {
                const k = `skl::${d.domain}::${s.skill}`;
                if (selected.has(k)) {
                    trajectories.push({
                        key: k, label: s.skill, sub: `${d.domain} · ${s.correct}/${s.attempted} lifetime`,
                        color: d.color, dash: dashSeq[di % dashSeq.length], w: 2, points: toPoints(s.values),
                    });
                    di++;
                }
            });
        }
    });
    // Plotted lines get distinct colors from a qualitative palette — sibling
    // skills/categories share a domain/section base color, which made concurrent
    // lines nearly indistinguishable. Colors are assigned in the order topics
    // were SELECTED (the Set preserves insertion order), so a newly picked topic
    // takes the next palette color instead of reshuffling existing lines.
    // Domain lines stay solid, skill lines dashed.
    const LINE_PALETTE = [
        '#0575a2', '#a23205', '#6b21a8', '#0f7b6c', '#d34053',
        '#f2994a', '#2f80ed', '#7FB347', '#b8860b', '#e75480',
        '#1C2434', '#57cbf5',
    ];
    const selectionOrder = Array.from(selected);
    trajectories.forEach(t => {
        t.color = LINE_PALETTE[Math.max(0, selectionOrder.indexOf(t.key)) % LINE_PALETTE.length];
    });
    // Picker swatches mirror the plotted color for selected rows (gray when not
    // plotted) so the left panel always matches the chart.
    const plottedColor = new Map(trajectories.map(t => [t.key, t.color]));
    const UNPLOTTED = '#c8ccd2';

    const PL = 44, PR = 20, PT = 18, PB = 50, H = 320;
    const W = Math.max(440, PL + PR + N * 64);
    const iW = W - PL - PR, iH = H - PT - PB;
    const xF = (i: number) => PL + (iW / Math.max(1, N - 1)) * i;
    const yF = (v: number) => PT + (1 - v) * iH;
    const [hover, setHover] = useState<{ ti: number; pi: number } | null>(null);

    if (!N) return null;

    return (
        <div className='r-card'>
            <div className='r-card__head'><div>
                <div className='r-card__title'>Accuracy Over Time</div>
                <div className='r-card__sub'>
                    {flat
                        ? `Pick any reporting categories to plot % correct across all ${N} practice tests. Each line marks only the tests where that category appeared.`
                        : `Pick any domains or skills to plot % correct across all ${N} practice tests. Domain lines are solid; skill lines are dashed and only mark the tests where the skill appeared. Skills with thin evidence are grayed out.`}
                </div>
            </div></div>
            <div className='r-expwrap'>
                {/* ── Picker ── */}
                <div style={{ background: '#FAFBFC', border: '1px solid #EAECF0', borderRadius: 10, padding: '12px 12px 8px', maxHeight: 460, overflow: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#676767', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            Selected ({selected.size})
                        </div>
                        <button
                            onClick={() => setSelected(new Set())}
                            disabled={selected.size === 0}
                            style={{ background: 'transparent', border: 'none', color: selected.size === 0 ? '#c8ccd2' : '#0575a2', fontSize: 11, fontWeight: 700, fontFamily: 'inherit', cursor: selected.size === 0 ? 'default' : 'pointer', padding: 0 }}
                        >Clear</button>
                    </div>
                    {meta.sections.map(sec => {
                        const sectionDomains = data.domains.filter(d => d.section === sec.key);
                        if (!sectionDomains.length) return null;
                        return (
                            <div key={sec.key} style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#828282', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 4px 4px', borderBottom: '1px solid #EAECF0', marginBottom: 4 }}>
                                    {sec.label}
                                </div>
                                {sectionDomains.map(d => {
                                    if (flat) {
                                        // Single-level: one selectable category row, no expander.
                                        const sk = (skillsByDom[d.domain] || [])[0];
                                        const disabled = !!sk && sk.attempted < 2;
                                        const on = selected.has(`dom::${d.domain}`);
                                        const lineColor = on ? (plottedColor.get(`dom::${d.domain}`) || d.color) : UNPLOTTED;
                                        return (
                                            <label
                                                key={d.domain}
                                                title={disabled ? 'Needs more data (seen once)' : (sk ? `${sk.correct}/${sk.attempted} correct lifetime` : '')}
                                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', borderRadius: 4, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1, background: on ? '#fff' : 'transparent', border: `1px solid ${on ? '#EAECF0' : 'transparent'}` }}
                                            >
                                                <input type='checkbox' checked={on} disabled={disabled} onChange={() => toggleKey(`dom::${d.domain}`)} style={{ accentColor: lineColor, margin: 0 }} />
                                                <span style={{ width: 12, height: 0, flexShrink: 0, borderTop: `2.5px solid ${lineColor}` }} />
                                                <span style={{ fontSize: 12, fontWeight: 600, color: '#1C2434', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.domain}</span>
                                                {sk && <span style={{ fontSize: 10, color: '#828282', marginLeft: 'auto', flexShrink: 0 }}>{sk.correct}/{sk.attempted}</span>}
                                            </label>
                                        );
                                    }
                                    const skills = skillsByDom[d.domain] || [];
                                    const selectableKeys = [`dom::${d.domain}`, ...skills.filter(s => s.attempted >= 2).map(s => `skl::${d.domain}::${s.skill}`)];
                                    const onCount = selectableKeys.filter(k => selected.has(k)).length;
                                    const isOpen = openDomains.has(d.domain);
                                    const allOn = onCount === selectableKeys.length;
                                    return (
                                        <div key={d.domain} style={{ marginBottom: 2 }}>
                                            <button
                                                onClick={() => toggleDomain(d.domain)}
                                                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', padding: '6px 4px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
                                            >
                                                <svg width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='#676767' strokeWidth='3' style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .12s', flexShrink: 0 }}>
                                                    <polyline points='9,6 15,12 9,18' />
                                                </svg>
                                                <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                                                <span style={{ fontSize: 12, fontWeight: 600, color: '#1C2434', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.domain}</span>
                                                <span style={{ fontSize: 10, color: '#828282', marginLeft: 'auto', flexShrink: 0 }}>{onCount}/{selectableKeys.length}</span>
                                            </button>
                                            {isOpen && (
                                                <div style={{ paddingLeft: 22, paddingBottom: 4 }}>
                                                    <button
                                                        onClick={() => selectAllInDomain(d.domain)}
                                                        style={{ background: 'transparent', border: 'none', color: '#0575a2', fontSize: 10, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', padding: '2px 4px', marginBottom: 2, display: 'block' }}
                                                    >{allOn ? 'Deselect all' : 'Select all in domain'}</button>
                                                    {[
                                                        { k: `dom::${d.domain}`, label: 'Domain overall', dash: false, disabled: false, title: '' },
                                                        ...skills.map(s => ({
                                                            k: `skl::${d.domain}::${s.skill}`,
                                                            label: s.skill,
                                                            dash: true,
                                                            disabled: s.attempted < 2,
                                                            title: s.attempted < 2 ? 'Needs more data (seen once)' : `${s.correct}/${s.attempted} correct lifetime`,
                                                        })),
                                                    ].map(row => {
                                                        const on = selected.has(row.k);
                                                        const lineColor = on ? (plottedColor.get(row.k) || d.color) : UNPLOTTED;
                                                        return (
                                                            <label
                                                                key={row.k}
                                                                title={row.title}
                                                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px', borderRadius: 4, cursor: row.disabled ? 'default' : 'pointer', opacity: row.disabled ? 0.45 : 1, background: on ? '#fff' : 'transparent', border: `1px solid ${on ? '#EAECF0' : 'transparent'}` }}
                                                            >
                                                                <input type='checkbox' checked={on} disabled={row.disabled} onChange={() => toggleKey(row.k)} style={{ accentColor: lineColor, margin: 0 }} />
                                                                <span style={{ width: 12, height: 0, flexShrink: 0, borderTop: `2.5px ${row.dash ? 'dashed' : 'solid'} ${lineColor}` }} />
                                                                {/* paddingRight keeps the italic overhang from being clipped by overflow:hidden */}
                                                                <span style={{ fontSize: 12, color: '#1C2434', fontStyle: row.dash ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 4 }}>{row.label}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                {/* ── Chart ── */}
                <div style={{ minWidth: 0 }}>
                    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
                            {[0, 25, 50, 75, 100].map(g => (
                                <g key={g}>
                                    <line x1={PL} x2={W - PR} y1={yF(g / 100)} y2={yF(g / 100)} stroke={g === 0 ? '#EAECF0' : '#f3f3f3'} />
                                    <text x={PL - 8} y={yF(g / 100) + 4} textAnchor='end' fontSize='11' fill='#828282'>{g}%</text>
                                </g>
                            ))}
                            {trajectories.length === 0 && (
                                <text x={PL + iW / 2} y={PT + iH / 2} textAnchor='middle' fontSize='13' fill='#828282'>Pick a {flat ? 'category' : 'domain or skill'} on the left to plot it.</text>
                            )}
                            {trajectories.map((t, ti) => {
                                const pts = t.points.map(p => `${xF(p.i)},${yF(p.v)}`).join(' ');
                                return (
                                    <g key={t.key}>
                                        {t.points.length > 1 && (
                                            <polyline points={pts} fill='none' stroke={t.color} strokeWidth={t.w} strokeDasharray={t.dash || undefined} strokeLinecap='round' strokeLinejoin='round' />
                                        )}
                                        {t.points.map((p, pi) => (
                                            <circle
                                                key={pi}
                                                cx={xF(p.i)} cy={yF(p.v)}
                                                r={hover && hover.ti === ti && hover.pi === pi ? 6 : 3.5}
                                                fill={t.color} stroke='#fff' strokeWidth='1.5'
                                                onMouseEnter={() => setHover({ ti, pi })}
                                                onMouseLeave={() => setHover(null)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </g>
                                );
                            })}
                            {tests.map((t, i) => (
                                <g key={i} transform={`translate(${xF(i)},${H - PB + 14})`}>
                                    <text textAnchor='end' fontSize='10' fill='#828282' transform='rotate(-35)'>{fmtDate(t.date)}</text>
                                </g>
                            ))}
                            {hover && trajectories[hover.ti] && (() => {
                                // Compact tooltip: topic name + test name + "% · date". Nothing else.
                                const t = trajectories[hover.ti];
                                const p = t.points[hover.pi];
                                const x = xF(p.i), y = yF(p.v);
                                const name = t.label.length > 28 ? `${t.label.slice(0, 27)}…` : t.label;
                                const testName = stripKind(tests[p.i].name);
                                const line3 = `${Math.round(p.v * 100)}% · ${fmtDate(tests[p.i].date)}`;
                                const bw = Math.max(name.length * 6.4, testName.length * 6.0, line3.length * 6.0) + 20;
                                const tx = x > W * 0.6 ? x - bw - 10 : x + 10;
                                return (
                                    <g pointerEvents='none'>
                                        <rect x={tx} y={y - 61} width={bw} height='53' rx='8' fill='#1C2434' />
                                        <text x={tx + 10} y={y - 46} fontSize='11' fontWeight='700' fill='#fff'>{name}</text>
                                        <text x={tx + 10} y={y - 31} fontSize='11' fill='#cdd5e0'>{testName}</text>
                                        <text x={tx + 10} y={y - 16} fontSize='11' fill='#aadddd'>{line3}</text>
                                    </g>
                                );
                            })()}
                        </svg>
                    </div>
                    {trajectories.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                            {trajectories.map(t => (
                                <span key={t.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 8px', background: '#fff', border: '1px solid #EAECF0', borderRadius: 40, fontSize: 12, color: '#1C2434' }}>
                                    <span style={{ width: 18, height: 0, borderTop: `2.5px ${t.dash ? 'dashed' : 'solid'} ${t.color}`, display: 'inline-block' }} />
                                    <span style={{ fontWeight: 600 }}>{t.label}</span>
                                    <span style={{ color: '#828282', fontSize: 11 }}>· {t.sub.split(' · ')[0]}</span>
                                    <button
                                        onClick={() => toggleKey(t.key)}
                                        title='Remove'
                                        style={{ background: 'transparent', border: 'none', color: '#828282', cursor: 'pointer', padding: 0, marginLeft: 2, display: 'inline-flex' }}
                                    >
                                        <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3'>
                                            <line x1='18' y1='6' x2='6' y2='18' /><line x1='6' y1='6' x2='18' y2='18' />
                                        </svg>
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function PerformanceHistory({ data }: { data: PlanData }) {
    return (
        <div className='r-wrap' style={{ paddingTop: 0, gap: 18 }}>
            <ScoreChart data={data} />
            <AccuracyExplorer data={data} />
        </div>
    );
}

export default PerformanceHistory;
